import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, BookOpen, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, EmptyState, Pagination, PageLoader, SearchInput } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RequestDialog } from "@/components/RequestDialog";

export function TutorCard({ t, profileBase, onRequest }) {
  return (
    <div className="lift group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white" data-testid={`tutor-card-${t.id}`}>
      <div className="relative h-44 overflow-hidden bg-[#0B1A33]">
        {t.profile_picture ? (
          <img src={t.profile_picture.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${t.profile_picture}` : t.profile_picture} alt={t.name} className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid-texture flex h-full items-center justify-center"><Avatar name={t.name} size="h-20 w-20" className="text-2xl" /></div>
        )}
        <span className="absolute left-3 top-3 rounded-full border border-[#1F6FFF]/30 bg-[#0B1A33]/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#00D8F6] backdrop-blur-sm">{t.subject}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg font-bold text-[#0B1A33]" data-testid={`tutor-card-name-${t.id}`}>{t.name}</h3>
        <p className="text-sm text-[#1F6FFF]">{t.specialization}</p>
        <div className="mt-3 space-y-1.5 text-sm text-slate-600">
          <p className="flex items-center gap-2"><Award className="h-4 w-4 text-[#3CC34A]" />{t.qualification || "—"}</p>
          <p className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[#3CC34A]" />{t.experience} yrs experience</p>
        </div>
        <div className="mt-5 flex gap-2 pt-1">
          <Link to={`${profileBase}/${t.id}`} data-testid={`tutor-card-view-button-${t.id}`} className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-center text-sm font-semibold text-[#0B1A33] transition-colors hover:border-[#0B1A33]">View Profile</Link>
          <button onClick={() => onRequest(t)} data-testid={`tutor-card-request-button-${t.id}`} className="flex-1 rounded-full bg-[#1F6FFF] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1450C9]">Request Session</button>
        </div>
      </div>
    </div>
  );
}

export function TutorBrowser({ profileBase }) {
  const [f, setF] = useState({ q: "", subject: "all", specialization: "", min_experience: "0" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [req, setReq] = useState(null);

  useEffect(() => {
    const id = setTimeout(async () => {
      const params = { q: f.q, specialization: f.specialization, min_experience: f.min_experience, page, limit: 9,
        subject: f.subject === "all" ? "" : f.subject };
      const { data } = await api.get("/tutors", { params });
      setData(data);
    }, 250);
    return () => clearTimeout(id);
  }, [f, page]);

  const set = (k) => (v) => { setPage(1); setF((p) => ({ ...p, [k]: v })); };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 md:flex-row md:items-center">
        <SearchInput value={f.q} onChange={set("q")} placeholder="Tutor name" testid="tutor-search-input" />
        <Select value={f.subject} onValueChange={set("subject")}>
          <SelectTrigger className="h-10 rounded-full md:w-48" data-testid="tutor-subject-filter"><SelectValue placeholder="Subject" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {(data?.subjects || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={f.specialization} onChange={(e) => set("specialization")(e.target.value)} placeholder="Specialization" className="h-10 rounded-full md:w-48" data-testid="tutor-specialization-filter" />
        <Select value={f.min_experience} onValueChange={set("min_experience")}>
          <SelectTrigger className="h-10 rounded-full md:w-44" data-testid="tutor-experience-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["0", "Any experience"], ["3", "3+ years"], ["5", "5+ years"], ["8", "8+ years"], ["10", "10+ years"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!data ? <PageLoader /> : data.items.length === 0 ? (
        <EmptyState title="No tutors match your filters" text="Try a different subject or clear the filters." testid="tutors-empty" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="tutor-grid">
            {data.items.map((t) => <TutorCard key={t.id} t={t} profileBase={profileBase} onRequest={setReq} />)}
          </div>
          {data.pages > 1 && <div className="mt-6 rounded-2xl bg-white"><Pagination page={data.page} pages={data.pages} total={data.total} onPage={setPage} /></div>}
        </>
      )}
      <RequestDialog tutor={req} onClose={() => setReq(null)} />
    </div>
  );
}

export const SubjectIcon = BookOpen;
